import React, {useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Box,
} from '@mui/material';

type DefectSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type DefectStatus =
  | 'IDENTIFIED'
  | 'IN_REVIEW'
  | 'ACCEPTED'
  | 'RESOLVED'
  | 'REJECTED';

interface DefectFormData {
  title: string;
  description: string;
  severity: DefectSeverity;
  defectStatus: DefectStatus;
  roomType: string;
  defectCategory: string;
  sourceClipIndex?: number;
  sourceFramePosition?: string;
}

interface AddDefectDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DefectFormData) => void;
  initialData?: Partial<DefectFormData>;
  isEditing?: boolean;
  loading?: boolean;
  currentClipIndex?: number;
  currentFramePosition?: string;
}

const severityOptions: DefectSeverity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const statusOptions: DefectStatus[] = [
  'IDENTIFIED',
  'IN_REVIEW',
  'ACCEPTED',
  'RESOLVED',
  'REJECTED',
];
const roomTypeOptions = [
  'Living Room',
  'Bedroom',
  'Kitchen',
  'Bathroom',
  'Dining Room',
  'Office',
  'Garage',
  'Basement',
  'Attic',
  'Exterior',
  'Garden',
  'Hallway',
  'Staircase',
  'Laundry Room',
  'Storage',
  'General',
];
const categoryOptions = [
  'Structural',
  'Electrical',
  'Plumbing',
  'HVAC',
  'Roofing',
  'Windows/Doors',
  'Flooring',
  'Walls/Ceilings',
  'Paint/Finish',
  'Fixtures',
  'Appliances',
  'Safety',
  'Moisture/Water',
  'Pest',
  'Other',
];

export function AddDefectDialog({
  open,
  onClose,
  onSubmit,
  initialData,
  isEditing = false,
  loading = false,
  currentClipIndex,
  currentFramePosition,
}: AddDefectDialogProps) {
  const [formData, setFormData] = useState<DefectFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    severity: initialData?.severity || 'MEDIUM',
    defectStatus: initialData?.defectStatus || 'IDENTIFIED',
    roomType: initialData?.roomType || '',
    defectCategory: initialData?.defectCategory || '',
    sourceClipIndex: initialData?.sourceClipIndex ?? currentClipIndex,
    sourceFramePosition:
      initialData?.sourceFramePosition ?? currentFramePosition,
  });

  const handleChange = (
    field: keyof DefectFormData,
    value: string | number
  ) => {
    setFormData((prev) => ({...prev, [field]: value}));
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) return;
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      severity: 'MEDIUM',
      defectStatus: 'IDENTIFIED',
      roomType: '',
      defectCategory: '',
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Defect' : 'Add Defect'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{mt: 1}}>
          <TextField
            label="Title"
            required
            fullWidth
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="e.g., Cracked wall near window"
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Detailed description of the defect..."
          />

          <Box display="flex" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select
                value={formData.severity}
                label="Severity"
                onChange={(e) =>
                  handleChange('severity', e.target.value as DefectSeverity)
                }
              >
                {severityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isEditing && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.defectStatus}
                  label="Status"
                  onChange={(e) =>
                    handleChange('defectStatus', e.target.value as DefectStatus)
                  }
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Box display="flex" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Room Type</InputLabel>
              <Select
                value={formData.roomType}
                label="Room Type"
                onChange={(e) => handleChange('roomType', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select room</em>
                </MenuItem>
                {roomTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.defectCategory}
                label="Category"
                onChange={(e) => handleChange('defectCategory', e.target.value)}
              >
                <MenuItem value="">
                  <em>Select category</em>
                </MenuItem>
                {categoryOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {(formData.sourceClipIndex !== undefined ||
            formData.sourceFramePosition) && (
            <Box display="flex" gap={2}>
              <TextField
                label="Clip Index"
                type="number"
                fullWidth
                value={formData.sourceClipIndex ?? ''}
                onChange={(e) =>
                  handleChange('sourceClipIndex', parseInt(e.target.value) || 0)
                }
              />
              <TextField
                label="Frame Position"
                fullWidth
                value={formData.sourceFramePosition ?? ''}
                onChange={(e) =>
                  handleChange('sourceFramePosition', e.target.value)
                }
                placeholder="e.g., top-left"
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title.trim()}
        >
          {loading ? 'Saving...' : isEditing ? 'Update' : 'Add Defect'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
